import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  OnboardingState,
  BrainParams,
  KnowledgeSources,
  TrainingProgress,
  TrainingStep,
} from '@/modules/onboarding/model';

interface BrainParamUpdate {
  field: keyof BrainParams;
  value: BrainParams[keyof BrainParams];
}

const defaultBrainParams: BrainParams = {
  brain_name: '',
  company_name: '',
  goal: '',
  response_length: 'standard',
  tone_type: 'neutral',
  response_language: 'en',
  match_user_language: false,
};

const defaultTrainingProgress: TrainingProgress = {
  currentStep: 0,
  totalSteps: 0,
  isComplete: false,
  isTraining: false,
  steps: [],
  progressPercent: 0,
  hasErrors: false,
};

export const defaultState: OnboardingState = {
  brain_type: null,
  brain_params: defaultBrainParams,
  knowledge_sources: { website: '', files: [] },
  integrations: [],
  created_brain_id: null,
  created_collection_id: null,
  created_datasource_ids: [],
  desk_id: null,
  integration_id: null,
  training_status: 'idle',
  training_progress: defaultTrainingProgress,
  is_invitation: false,
};

const slice = createSlice({
  name: 'onboarding',
  initialState: defaultState,
  reducers: {
    setTemplateBrainId: (state, action) => {
      state.brain_type = action.payload;
    },

    setDeskId: (state, action) => {
      state.desk_id = action.payload;
    },

    setIntegrationId: (state, action) => {
      state.integration_id = action.payload;
    },

    setCreatedBrainId: (state, action) => {
      state.created_brain_id = action.payload;
    },

    setCreatedCollectionId: (state, action) => {
      state.created_collection_id = action.payload;
    },

    addCreatedDatasourceId: (state, action) => {
      state.created_datasource_ids.push(action.payload);
    },

    setBrainParams: (state, action: PayloadAction<BrainParams>) => {
      state.brain_params = action.payload;
    },

    updateBrainParam: (state, action: PayloadAction<BrainParamUpdate>) => {
      const { field, value } = action.payload;
      // @ts-expect-error - TODO: fix this
      state.brain_params[field] = value;
    },

    setKnowledgeSources: (state, action: PayloadAction<KnowledgeSources>) => {
      state.knowledge_sources = action.payload;
    },

    setIntegrations: (state, action: PayloadAction<string[]>) => {
      state.integrations = action.payload;
    },

    addIntegration: (state, action: PayloadAction<string>) => {
      if (!state.integrations.includes(action.payload)) {
        state.integrations.push(action.payload);
      }
    },

    removeIntegration: (state, action: PayloadAction<string>) => {
      state.integrations = state.integrations.filter((i) => i !== action.payload);
    },

    setTrainingStatus: (state, action: PayloadAction<OnboardingState['training_status']>) => {
      state.training_status = action.payload;
    },

    // Training progress actions
    initializeTrainingSteps: (state, action: PayloadAction<TrainingStep[]>) => {
      // Only initialize if not currently training or if steps have changed
      if (
        !state.training_progress.isTraining ||
        state.training_progress.steps.length !== action.payload.length
      ) {
        state.training_progress = {
          currentStep: 0,
          totalSteps: action.payload.length,
          isComplete: false,
          isTraining: state.training_progress.isTraining, // Preserve current training state
          steps: action.payload,
          progressPercent: 0,
          hasErrors: false,
        };
      }
    },

    startTraining: (state) => {
      state.training_progress.isTraining = true;
    },
    setIsInvitation: (state, action: PayloadAction<boolean>) => {
      state.is_invitation = action.payload;
    },

    updateTrainingStep: (state, action: PayloadAction<{ stepIndex: number }>) => {
      const { stepIndex } = action.payload;
      const updatedSteps = state.training_progress.steps.map((step, idx) => {
        let status: TrainingStep['status'] = 'pending';

        if (step.status === 'failed') {
          status = 'failed';
        } else if (idx < stepIndex) {
          status = 'complete';
        } else if (idx === stepIndex) {
          status = 'processing';
        }

        return { ...step, status };
      });

      state.training_progress = {
        ...state.training_progress,
        currentStep: stepIndex,
        steps: updatedSteps,
        progressPercent: (stepIndex / state.training_progress.totalSteps) * 100,
      };
    },

    markStepFailed: (state, action: PayloadAction<{ stepIndex: number }>) => {
      const { stepIndex } = action.payload;
      if (state.training_progress.steps[stepIndex]) {
        state.training_progress.steps[stepIndex].status = 'failed';
        state.training_progress.hasErrors = true;
      }
    },

    completeTraining: (state) => {
      const completedSteps = state.training_progress.steps.map((step) => ({
        ...step,
        // Preserve failed status, mark others as complete
        status: step.status === 'failed' ? ('failed' as const) : ('complete' as const),
      }));

      state.training_progress = {
        ...state.training_progress,
        currentStep: state.training_progress.totalSteps,
        isComplete: true,
        isTraining: false,
        steps: completedSteps,
        progressPercent: 100,
      };
    },

    resetTrainingProgress: (state) => {
      state.training_progress = defaultTrainingProgress;
    },

    resetTrainingToPreview: (state) => {
      // Reset training state but keep the steps for preview
      state.training_progress.isTraining = false;
      state.training_progress.isComplete = false;
      state.training_progress.currentStep = 0;
      state.training_progress.progressPercent = 0;
      state.training_progress.hasErrors = false;
      // Reset all step statuses to pending
      state.training_progress.steps = state.training_progress.steps.map((step) => ({
        ...step,
        status: 'pending' as const,
      }));
    },

    // reset state. pass a specific key to reset only that key, if not passed, reset all
    reset: (state, action) => {
      if (action.payload) {
        state[action.payload] = defaultState[action.payload];
      } else {
        Object.assign(state, defaultState);
      }
    },
  },
});

export const {
  setTemplateBrainId,
  setDeskId,
  setIntegrationId,
  setCreatedBrainId,
  setCreatedCollectionId,
  addCreatedDatasourceId,
  setBrainParams,
  updateBrainParam,
  setKnowledgeSources,
  setIntegrations,
  addIntegration,
  removeIntegration,
  setTrainingStatus,
  initializeTrainingSteps,
  startTraining,
  updateTrainingStep,
  markStepFailed,
  completeTraining,
  resetTrainingProgress,
  resetTrainingToPreview,
  reset,
} = slice.actions;

export default slice.reducer;
